import React, { useRef, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { db } from "../../firebaseConfig";
import { useAuth } from '../../AuthContext';
import { useAppSelector } from '../../redux/store';
import { fetchLeadCampaignAnalytics, LeadData } from './Function';
import { collection, DocumentData, getDocs } from 'firebase/firestore';
import getCurrentScreen from '../../Helper/GetScreen';

interface UserOption {
  id: string;
  name: string;
}

interface CampaignAnalyticsProps {
  users: UserOption[];
  settings: any;
}

const CampaignDistribution: React.FC<CampaignAnalyticsProps> = ({ users, settings }) => {
  const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
  const customDatePopupRef = useRef<HTMLDivElement>(null);

  const [customDatePopupOpen, setCustomDatePopupOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filter, setFilter] = useState<boolean>(false);
  const [stateData, setStateData] = React.useState<DocumentData[]>();
  const [leadData, setLeadData] = React.useState<LeadData>();

  useEffect(() => {
    setStartDate(new Date());
    setEndDate(new Date());
  }, [userData?.companyId, users, currentScreen]);

  const handleDateRange = (date: Date | null, type: string) => {
    if (!date) return null;

    if (type === 'start') {
      date.setHours(5, 30, 0, 0);
    } else {
      // Add one day for end date
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(5, 29, 59, 0);
      return nextDay.toISOString();
    }

    return date.toISOString();
  };

  const handleApplyFilters = () => {
    setFilter(true);
    setCustomDatePopupOpen(false);
  };

  const handleDateRangeClick = () => {
    setCustomDatePopupOpen(!customDatePopupOpen);
  };


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customDatePopupRef.current && !customDatePopupRef.current.contains(event.target as Node)) {
        setCustomDatePopupOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchCompanyLeadStates = async () => {
      const companyLeadStatesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadStates`));
      const companyLeadStates = companyLeadStatesSnapshot.docs.map(doc => doc.data());
      setStateData(companyLeadStates);
    };
    fetchCompanyLeadStates();
  }, [userData?.companyId]);

  useEffect(() => {
    fetchLeadCampaignAnalytics({
      db,
      userData,
      startTime: handleDateRange(startDate, 'start') || null,
      endTime: handleDateRange(endDate, 'end') || null,
      users,
      currentScreen,
      settingsData: settings?.campaigns,
    })
      .then((data) => { setLeadData(data); })
      .catch((error) => console.error("Error fetching call logs:", error));
  }, [userData?.companyId, startDate, endDate, users, currentScreen, filter]);

  return (
    <div className="bg-white relative">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div
            className="flex items-center justify-center w-10 h-10 rounded-full"
            style={{
              background: 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)',
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/desktop/step.svg`}
              alt="Icon"
              className="w-6 h-6"
            />
          </div>
          <h2 className="text-2xl text-black">Campaign Analytics</h2>
        </div>

        <div
          className="w-full sm:w-auto py-2 px-1 border border-cyan-600 text-sm rounded-lg bg-white cursor-pointer"
          onClick={handleDateRangeClick}
        >
          {startDate === null && endDate === null
            ? "Lifetime"
            : startDate && endDate
              ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
              : "Select Date Range"}
        </div>
      </div>

      {customDatePopupOpen && (
        <div
          ref={customDatePopupRef}
          className="absolute top-10 right-0 mt-2 w-auto h-auto bg-white border rounded-3xl shadow-lg p-4 z-[50]"
        >
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-[14px] font-normal leading-[20px] text-left">
              Select Date Range
            </h3>
          </div>

          <div className="mb-1">
            <span className="text-[12px] font-normal leading-[16px] text-left">
              <span className='font-medium'>Current: </span>
              {startDate && endDate
                ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                : "Select Date Range"}
            </span>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button onClick={() => { setStartDate(new Date()); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Today</button>
              <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 7))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 7 Days</button>
              <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 30))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 30 Days</button>
              <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 90))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 90 Days</button>
              {/* Lifetime filter */}
              <button onClick={() => { setStartDate(null); setEndDate(null); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Lifetime</button>
            </div>
            <div
              className="p-4 bg-white shadow-lg rounded-[12px]"
              style={{ width: '100%', height: 'auto' }}
            >
              <DatePicker
                selected={startDate}
                startDate={startDate || undefined}
                endDate={endDate || undefined}
                selectsRange
                onChange={(update) => {
                  const [start, end] = update;
                  setStartDate(start);
                  setEndDate(end);
                }}
                inline
                className="w-full h-full p-4 bg-gray-100 shadow-lg rounded-[12px] custom-calendar"
                maxDate={new Date()}
              />
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <button
              className="px-8 py-2 text-white bg-black rounded-full"
              onClick={() => setCustomDatePopupOpen(false)}
            >
              Cancel
            </button>
            <button
              className="px-8 py-2 text-white bg-cyan-600 rounded-full"
              onClick={handleApplyFilters}
            >
              Apply
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 mb-4 bg-gray-50 rounded-lg p-3">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Lead States</h3>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          {stateData?.map((state, index) => (
            <div key={index} className="flex items-center gap-2 min-w-[120px]">
              <div
                className="w-3 h-3 rounded-full ring-1 ring-gray-200"
                style={{ background: state.color }}
              />
              <span className="text-sm text-gray-600">{state.state}</span>
            </div>
          ))}
          <div className="flex items-center gap-2 min-w-[120px]">
            <div
              className="w-3 h-3 rounded-full ring-1 ring-gray-200"
              style={{ background: "#000000" }}
            />
            <span className="text-sm text-gray-600">No State</span>
          </div>
        </div>
      </div>

      <div className="mt-6 border border-cyan-600 rounded-lg overflow-hidden">
        <div className="max-h-[500px] overflow-y-auto">
          <table className="w-full relative">
            <thead className="sticky top-0 bg-white z-[5]">
              <tr className="border-b border-cyan-600">
                <th className="py-2 px-4 font-semibold text-left w-1/3">Campaign Name</th>
                <th className="py-2 px-4 font-semibold text-left w-1/3 border-l border-cyan-600">Total Leads</th>
                <th className="py-2 px-4 font-semibold text-left w-1/3 border-l border-cyan-600">Lead Distribution</th>
              </tr>
            </thead>
            <tbody>
              {leadData?.campaignMapArray?.map((campaign, index) => (
                <tr key={index} className="border-b border-cyan-600 last:border-b-0">
                  <td className="py-3 px-4 text-sm">{campaign.campaign}</td>
                  <td className="py-3 px-4 text-sm border-l border-cyan-600">{campaign?.count}</td>
                  <td className="py-3 px-4 border-l border-cyan-600">
                    <div className="w-full h-4 bg-gray-200 rounded-full overflow-visible">
                      <div className="flex h-full relative">
                        {campaign.statePercentageData
                          .filter(state => {
                            // Ensure filtering out states with 0% or 0.00%
                            const percentage = Number(state.percentage.replace('%', ''));
                            return percentage > 0;
                          })
                          .map((state, index, filteredArray) => {
                            const percentage = Number(state.percentage.split('%')[0]);
                            const isFirst = index === 0;
                            const isLast = index === filteredArray.length - 1;
                            const isOnly = filteredArray.length === 1;
                            const roundedClasses =
                              isOnly ? 'rounded-full' :
                                isFirst ? 'rounded-l-full' :
                                  isLast ? 'rounded-r-full' : '';

                            return (
                              <div
                                key={index}
                                className="h-full group relative transform-gpu"
                                style={{
                                  width: `${percentage}%`,
                                }}
                              >
                                <div
                                  className={`absolute inset-0 transition-all duration-200 transform origin-center hover:scale-110 hover:shadow-lg group-hover:z-10 ${roundedClasses}`}
                                  style={{ background: state.color }}
                                />
                                <div
                                  className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-200 z-20"
                                >
                                  <div
                                    className="px-2 py-1 text-xs rounded-full shadow-lg text-white whitespace-nowrap"
                                    style={{ background: state.color }}
                                  >
                                    {percentage}%
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        }
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CampaignDistribution;